import React, { useContext, useEffect, useState } from 'react'
import { PaginationContent } from './components/PaginationContent'
import {
    CollectionDataLoaded,
    ContentDataTypes,
    DataLoaderGlobalParams,
    loadCollectionData,
} from '@news-mono/web-common'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import { CollectionDataContext } from '../../contexts'
import { useLocation } from 'react-router-dom'
import { breakpoints, useWindowWidth } from '@news-mono/component-library'

export interface PaginationProps {
    contextId: string
    dataDefinition: ContentDataTypes
    data: MaybeLoaded<CollectionDataLoaded>
    services: DataLoaderGlobalParams
}

export const Pagination = ({
    contextId,
    dataDefinition: initialDataDefinition,
    data: initialData,
    services,
}: PaginationProps) => {
    const { pathname } = useLocation()
    const { setCollectionData } = useContext(CollectionDataContext)
    const [data, setData] = useState(initialData)
    const [isMobile, setIsMobile] = useState(true)
    const [dataDefinition, setDataDefinition] = useState(
        excludeAggregated(initialDataDefinition),
    )
    const windowWidth = useWindowWidth()
    const paging =
        initialDataDefinition.type === 'listing'
            ? initialDataDefinition.paging
            : initialDataDefinition.type === 'edition'
            ? {
                  pageSize: initialDataDefinition.pageSize,
                  page: initialDataDefinition.page,
              }
            : { page: 1, pageSize: 15 }
    const pageSize = paging.pageSize
    const [currentPage, setCurrentPage] = useState(paging.page)

    async function goToPage(page: number) {
        if (
            dataDefinition &&
            dataDefinition.type === 'listing' &&
            dataDefinition.paging
        ) {
            const newDataDefinition = {
                ...dataDefinition,
                paging: {
                    ...dataDefinition.paging,
                    page,
                },
            }
            setDataDefinition(newDataDefinition)
            setCurrentPage(page)
            return await loadCollectionData(services, newDataDefinition)
        }
        if (dataDefinition && dataDefinition.type === 'edition') {
            const newDataDefinition = {
                ...dataDefinition,
                page,
            }
            setDataDefinition(newDataDefinition)
            setCurrentPage(page)
            return await loadCollectionData(services, newDataDefinition)
        }
        return undefined
    }

    async function loadData(page: number) {
        const result = await goToPage(page)
        if (result && result.kind === 'listing') {
            setData({
                loaded: true,
                result: {
                    publications: result.publications,
                    kind: 'listing',
                    loadMorePossible: result.loadMorePossible,
                    total: result.total,
                },
            })
        } else if (result && result.kind === 'edition') {
            setData({
                loaded: true,
                result: {
                    publications: [],
                    editions: result.editions,
                    kind: 'edition',
                    loadMorePossible: result.loadMorePossible,
                    total: result.total,
                },
            })
        } else {
            throw new Error('Error loading page')
        }
    }

    useEffect(() => {
        setCollectionData(contextId, data)
    }, [contextId, data, setCollectionData])
    useEffect(() => {
        setData(initialData)
        setDataDefinition(excludeAggregated(initialDataDefinition))
    }, [initialData, initialDataDefinition])
    useEffect(() => {
        if (!windowWidth) return
        windowWidth > breakpoints.sm ? setIsMobile(false) : setIsMobile(true)
    }, [windowWidth])

    return data.loaded &&
        (data.result.kind === 'listing' || data.result.kind === 'edition') &&
        data.result.total ? (
        <PaginationContent
            currentPage={currentPage}
            pageSize={pageSize}
            publicationCount={data.result.total}
            onPageChange={loadData}
            path={pathname}
            isMobile={isMobile}
        />
    ) : null
}

const excludeAggregated = (dataDefinition: ContentDataTypes) =>
    dataDefinition.type !== 'aggregated' ? dataDefinition : undefined
